import { useState, useEffect, useRef, useCallback } from 'react';

const useInfiniteScroll = (fetcher) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const observer = useRef();

    // fetchData accepts a page argument so it doesn't depend on the local `page` value
    const fetchData = useCallback(async (p) => {
        setLoading(true);
        setError(null);
        try {
            const { data, has_more } = await fetcher(p);
            setItems(prev => {
                const uniqueNewItems = data.filter(item => !prev.some(p => p.id === item.id));
                return [...prev, ...uniqueNewItems];
            });
            setHasMore(has_more);
        } catch (err) {
            console.error("Fetch error details:", err);
            setError('Failed to fetch data. Please check the console for more details.');
        } finally {
            setLoading(false);
        }
    }, [fetcher]);

    // Trigger fetch when `page` changes (including initial mount with page=1)
    useEffect(() => {
        fetchData(page);
    }, [page, fetchData]);

    const loadingRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    return { items, loading, error, hasMore, loadingRef };
};

export default useInfiniteScroll;
